import { INVOICE_CARRIER_TYPE, INVOICE_ISSUING_TYPE } from "@constants/";
import { getOptionText, isEqual } from "@util/utils";
import { INVOICE_ISSUING_TYPE_OPTIONS, INVOICE_CARRIER_TYPE_OPTIONS } from "@icoach/pos/PosScreenPage.config";

/**
 * 計算單一購物車項目的小計金額
 *
 * @param {Object} item - 購物車項目
 * @param {number} item.currentPrice - 項目當前價格
 * @param {number} item.quantity - 項目數量
 * @returns {number} 項目小計金額
 */
export const calculateItemTotal = item => {
	return item.currentPrice * item.quantity;
};

/**
 * 發票顯示輔助函數
 * 根據發票類型和載具類型決定顯示的標題和副標題
 *
 * @param {Object} invoiceData - 發票資料
 * @param {number} invoiceData.issuingType - 發票類型數值
 * @param {string} invoiceData.issuingTypeText - 發票類型文字
 * @param {number} invoiceData.invoiceCarrierType - 載具類型數值
 * @param {string} invoiceData.invoiceCarrierTypeText - 載具類型文字
 * @param {string} invoiceData.invoiceCarrierNo - 載具號碼
 * @param {string} invoiceData.taxID - 統一編號
 * @param {string} invoiceData.issuingEmail - 發票通知信箱
 * @param {string} invoiceData.taxTitle - 發票抬頭
 * @returns {Object} 包含 title 和 subtitle 的物件
 */

export const getInvoiceDisplayInfo = invoiceData => {
	const { issuingType, issuingTypeText, invoiceCarrierType, invoiceCarrierTypeText, invoiceCarrierNo, taxID, issuingEmail, taxTitle } = invoiceData || {};

	// 如果沒有文字欄位，從數值轉換
	const finalIssuingTypeText = issuingTypeText || getOptionText(INVOICE_ISSUING_TYPE_OPTIONS, issuingType);
	const finalCarrierTypeText = invoiceCarrierTypeText || getOptionText(INVOICE_CARRIER_TYPE_OPTIONS, invoiceCarrierType);

	// 判斷是否為二聯式發票
	const isDuplicateInvoice = isEqual(issuingType, INVOICE_ISSUING_TYPE.DUPLICATEUNIFORMINVOICE);

	// 判斷是否為三聯式發票
	const isTriplicateInvoice = isEqual(issuingType, INVOICE_ISSUING_TYPE.TRIPLICATEUNIFORMINVOICE);

	if (isDuplicateInvoice) {
		// 二聯式發票邏輯
		if (isEqual(invoiceCarrierType, INVOICE_CARRIER_TYPE.MEMBERCARRIER)) {
			// 當 invoiceCarrierTypeText 為 二聯式且 invoiceCarrierTypeText 為會員載具
			if (issuingEmail) {
				// 有 issuingEmail 值
				return {
					title: issuingEmail,
					subtitle: finalCarrierTypeText,
				};
			} else {
				// issuingEmail 沒有值
				return {
					title: finalCarrierTypeText,
					subtitle: "",
				};
			}
		} else {
			// 當 invoiceCarrierTypeText 為 二聯式（非會員載具）
			return {
				title: invoiceCarrierNo,
				subtitle: finalCarrierTypeText,
			};
		}
	} else if (isTriplicateInvoice) {
		// 三聯式發票邏輯
		return {
			title: taxID,
			subtitle: taxTitle,
		};
	}

	// 預設情況：使用原有的邏輯作為後備
	return {
		title: invoiceCarrierNo || taxID || issuingEmail,
		subtitle: finalCarrierTypeText || finalIssuingTypeText || taxTitle,
	};
};

/**
 * 將後端原始商品資料轉換為前端購物車期望的格式 (ProductInput)
 * 假設後端欄位：productId, productCode, priceMarket, priceSell, productSpec
 */
export const adaptProductToCartItem = rawData => {
	// 這裡進行後端到前端欄位的映射，確保傳入 useCart 的格式是正確的
	const adapted = {
		id: `${rawData?.productID}${rawData?.variantID}`,
		sku: rawData?.sku,
		name: rawData?.productName,
		spec: rawData?.variantName,
		currentPrice: rawData?.price,
		fixedPrice: rawData?.fixedPrice,
		productID: rawData?.productID,
		variantID: rawData?.variantID,
	};

	return adapted;
};
